import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, ArrowLeft, CheckCircle2, HeartHandshake } from 'lucide-react';
import { API } from '../utils/apiBase';

export default function VolunteerApplyPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    availability: '', skills: '', why_volunteer: '', references: '',
    program: 'general', applicant_type: 'new',
    background_check_consent: false, website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [branding, setBranding] = useState({ hero_title: '', colors: { primary: '#6B3FA0', accent: '#9B7ED8', dark: '#2D1B4E' } });

  useEffect(() => {
    axios.get(`${API}/public/careers/branding`).then(r => setBranding(b => ({ ...b, ...r.data }))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/volunteers/apply`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: branding.colors.dark }}>
        <Card className="max-w-lg w-full">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4" style={{ color: branding.colors.primary }} />
            <h1 className="text-3xl font-bold mb-3" data-testid="volunteer-thanks">Thank you!</h1>
            <p className="text-gray-600 mb-6">
              Your volunteer application has been received. Our team will review it and reach out to you at
              {' '}<span className="font-medium text-gray-900">{form.email}</span> within a few business days.
            </p>
            <Button onClick={() => navigate('/careers')} data-testid="back-to-careers">
              Back to Careers
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <button
          onClick={() => navigate('/careers')}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Careers
        </button>

        <div className="flex items-center gap-3 mb-2">
          <HeartHandshake className="h-8 w-8" style={{ color: branding.colors.primary }} />
          <h1 className="text-4xl md:text-5xl font-bold">Volunteer with us</h1>
        </div>
        <p className="text-gray-600 mb-8 text-lg">
          Help us make a difference in the lives of individuals with intellectual and developmental disabilities.
          Tell us a bit about yourself and we'll be in touch.
        </p>

        <Card>
          <CardContent className="p-6 md:p-8">
            <form onSubmit={submit} className="space-y-5">
              <input type="text" name="website" value={form.website}
                     onChange={e => setField('website', e.target.value)}
                     tabIndex={-1} autoComplete="off"
                     style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={form.name} onChange={e => setField('name', e.target.value)}
                         required maxLength={120} data-testid="volunteer-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)}
                         required maxLength={200} data-testid="volunteer-email" />
                </div>
              </div>

              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setField('phone', e.target.value)}
                       maxLength={30} data-testid="volunteer-phone" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Which program interests you most? *</Label>
                  <select value={form.program} onChange={e => setField('program', e.target.value)}
                          className="w-full border rounded px-3 py-2" data-testid="volunteer-program">
                    <option value="general">Not sure yet / any</option>
                    <option value="day_program">Explore Program</option>
                    <option value="employment">Employment Services</option>
                    <option value="in_home">In-Home & Community Supports</option>
                    <option value="private_pay">Private Pay Programs</option>
                  </select>
                </div>
                <div>
                  <Label>Have you volunteered with us before?</Label>
                  <div className="flex gap-3 items-center h-10">
                    <label className="flex items-center gap-1.5 text-sm">
                      <input type="radio" name="applicant_type" value="new"
                             checked={form.applicant_type === 'new'}
                             onChange={e => setField('applicant_type', e.target.value)}
                             data-testid="volunteer-new" />
                      New volunteer
                    </label>
                    <label className="flex items-center gap-1.5 text-sm">
                      <input type="radio" name="applicant_type" value="returning"
                             checked={form.applicant_type === 'returning'}
                             onChange={e => setField('applicant_type', e.target.value)}
                             data-testid="volunteer-returning" />
                      Returning
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Label>When are you available?</Label>
                <Textarea value={form.availability} onChange={e => setField('availability', e.target.value)}
                          placeholder="e.g., Weekdays after 4pm, Saturday mornings"
                          rows={2} maxLength={2000} data-testid="volunteer-availability" />
              </div>

              <div>
                <Label>Skills or interests</Label>
                <Textarea value={form.skills} onChange={e => setField('skills', e.target.value)}
                          placeholder="e.g., art, music, cooking, transportation, admin"
                          rows={2} maxLength={2000} data-testid="volunteer-skills" />
              </div>

              <div>
                <Label>Why do you want to volunteer with us?</Label>
                <Textarea value={form.why_volunteer} onChange={e => setField('why_volunteer', e.target.value)}
                          rows={3} maxLength={2000} data-testid="volunteer-why" />
              </div>

              <div>
                <Label>References (optional)</Label>
                <Textarea value={form.references} onChange={e => setField('references', e.target.value)}
                          placeholder="Name, relationship, and contact info for 1-2 references"
                          rows={3} maxLength={2000} data-testid="volunteer-references" />
              </div>

              <label className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.background_check_consent}
                  onChange={e => setField('background_check_consent', e.target.checked)}
                  className="mt-1"
                  data-testid="bg-check-consent"
                />
                <span className="text-sm text-gray-700">
                  I understand that TNS Programs will run a background check (Act 33/34 + FBI) before I can begin volunteering,
                  and I consent to submit the required paperwork if my application moves forward.
                </span>
              </label>

              <Button type="submit" disabled={submitting} className="w-full"
                      style={{ background: branding.colors.primary }}
                      data-testid="submit-volunteer-app">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : 'Submit application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
